import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * SearchableSelect — Drop-in replacement for <select> with search/filter.
 *
 * Props:
 *   options     — Array of { value, label, sublabel? } objects
 *   value       — Currently selected value
 *   onChange    — (value) => void
 *   placeholder — Placeholder text when nothing is selected
 *   name        — Form field name (optional)
 *   required    — HTML required attribute (optional)
 *   className   — Additional classes for the container (optional)
 *   disabled    — Disable the component (optional)
 *
 * `sublabel` is secondary identity shown under the label in the list and
 * matched by the search — a vehicle's VIN, for instance, so a workshop
 * holding the carte grise can find the car without knowing its plate. It is
 * kept out of the closed trigger, which has one line to work with.
 */
/**
 * Accents are dropped before comparing.
 *
 * Client names here are Koné, Aïcha, Séverin, N'Guessan — and nobody types the
 * accent when they are searching. A literal `includes` means "kone" does not
 * find "Koné", so the name is in the list and the search says there is no
 * result. That reads as a broken dropdown, which is exactly what it is.
 */
const fold = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const digitsOf = (s) => String(s || '').replace(/\D/g, '');

/**
 * Every word of the query has to appear somewhere in the option — in any
 * order, so "amadou kone" finds "Koné Amadou".
 *
 * A word made of digits is also compared against the option's digits with all
 * punctuation removed, so a phone typed as "07 99 29" finds "0799290000".
 * People read numbers off a screen in groups and type them the same way.
 */
export function matchesQuery(option, query) {
  const tokens = fold(query).trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;

  const hay = fold(`${option?.label || ''} ${option?.sublabel || ''}`);
  const hayDigits = digitsOf(`${option?.label || ''} ${option?.sublabel || ''}`);

  return tokens.every((t) => {
    if (hay.includes(t)) return true;
    const d = digitsOf(t);
    return d.length > 0 && hayDigits.includes(d);
  });
}

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Rechercher...',
  name,
  required,
  className = '',
  disabled = false,
  // What to say when there is nothing to choose from at all. An empty list and
  // a search that matched nothing are different problems with different fixes,
  // and telling them apart is the difference between "add the client first"
  // and "check your spelling".
  emptyLabel = 'Aucune option disponible',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Where to draw the dropdown, in viewport coordinates.
  //
  // It is rendered into document.body rather than next to the trigger, because
  // <Panel> — which wraps almost every form in the app — carries
  // overflow-hidden for its rounded corners, and overflow-hidden clips an
  // absolutely-positioned descendant no matter what its z-index says. On a
  // short panel that meant the search box was visible and the list of results
  // underneath it was cut off completely: a dropdown that looked like it had
  // found nothing, whatever you typed.
  const [rect, setRect] = useState(null);

  const measure = () => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip above the trigger when the list would run off the bottom of the
    // window — near the foot of a long form it otherwise opens off-screen.
    const below = window.innerHeight - r.bottom;
    setRect({
      left: r.left,
      width: r.width,
      top: r.bottom + 4,
      bottom: window.innerHeight - r.top + 4,
      flip: below < 260 && r.top > below,
    });
  };

  const selectedOption = options.find(o => o.value === value);

  // Filter options by search text, across label AND sublabel — accent-blind,
  // word order free, and digit-aware. See matchesQuery.
  const filtered = !search.trim()
    ? options
    : options.filter((o) => matchesQuery(o, search));

  // Close on click outside. The list lives in a portal, so it is NOT inside
  // containerRef — checking only that would close the dropdown the instant you
  // clicked an option.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inList = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inList) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the search box, and keep the list glued to the trigger while the
  // page moves under it.
  useEffect(() => {
    if (!isOpen) return undefined;
    measure();
    if (inputRef.current) inputRef.current.focus();

    // Capture phase: the page scrolls inside <main>, not on window, so a
    // listener bound to window alone would never fire.
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  // Escape closes, which is what every native select does.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue, name);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', name);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value || ''}
          required={required}
          tabIndex={-1}
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          onChange={() => {}}
        />
      )}

      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="w-full h-[38px] px-3 rounded-[9px] border border-[#2B2724] bg-[#100F0D] text-left text-[13px]
                   text-[#F4F1EA] outline-none focus:border-[#4E4740] hover:border-[#3A342E]
                   flex items-center justify-between gap-2 transition-colors
                   disabled:opacity-50 disabled:cursor-not-allowed font-['IBM_Plex_Sans',system-ui,sans-serif]"
      >
        <span className={selectedOption ? 'text-[#F4F1EA] truncate' : 'text-[#5C554C] truncate'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="text-[#6E665C] hover:text-[#C9C3B6] transition-colors p-0.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg className={`w-[15px] h-[15px] text-[#8F877B] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown — portalled to <body>, see the note on `rect` above. */}
      {isOpen && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            ...(rect.flip ? { bottom: rect.bottom } : { top: rect.top }),
          }}
          className="z-[1000] rounded-xl border border-[#2B2724] bg-[#141210] shadow-2xl overflow-hidden"
        >
          {/* Search input */}
          <div className="p-2 border-b border-[#241F1B]">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="w-full h-[32px] px-2.5 rounded-[7px] border border-[#2B2724] bg-[#100F0D] text-[12.5px]
                         text-[#F4F1EA] placeholder:text-[#5C554C] outline-none focus:border-[#4E4740]"
            />
          </div>

          {/* Options list */}
          <ul className="max-h-48 overflow-y-auto">
            {options.length === 0 ? (
              // Nothing to choose from at all — a different problem from a
              // search that matched nothing, and it needs a different action.
              <li className="px-3 py-2.5 text-[12px] text-[#6E665C] text-center">{emptyLabel}</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-[12px] text-[#6E665C] text-center">
                Aucun résultat pour «&nbsp;{search.trim()}&nbsp;»
                <span className="block text-[10.5px] text-[#5C554C] mt-1">
                  {options.length} au total
                </span>
              </li>
            ) : (
              filtered.map(option => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`px-3 py-2 text-[12.5px] cursor-pointer transition-colors ${
                    option.value === value
                      ? 'bg-[#1E1B17] text-[#F4F1EA]'
                      : 'text-[#C9C3B6] hover:bg-[#1A1613] hover:text-[#F4F1EA]'
                  }`}
                >
                  <span className="block truncate">{option.label}</span>
                  {option.sublabel && (
                    <span className="block text-[10.5px] tracking-[.06em] text-[#6E665C] mt-0.5 truncate">
                      {option.sublabel}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
